import {
    InputLabelProps,
    InputProps,
    SxProps,
    Theme,
    ThemeOptions
} from '@mui/material';
import MuiCircularProgress from '@mui/material/CircularProgress';
import MuiFormHelperText from '@mui/material/FormHelperText';
import { inputLabelClasses as muiInputLabelClasses } from '@mui/material/InputLabel';
import { outlinedInputClasses as muiOutlinedInputClasses } from '@mui/material/OutlinedInput';
import MuiTextField, {
    TextFieldProps as MuiTextFieldProps
} from '@mui/material/TextField';
import { keys } from 'essential-utils';
import React, { forwardRef } from 'react';

import { Box, FlexBox } from '../box';
import { IconButton } from '../button';
import { useInitialShrink } from '../hooks';
import { LocaleOption } from '../language-picker';
import { ColorProp, useRubiksTheme } from '../theme';
import { firstLetterUppercase, sentence, TypographyProps } from '../typography';
import { deepMerge, enumType, makeEnum } from '../utils';
import { LocaleTextField } from './locale';
import { TextMask, TextMaskProps } from './mask';
import { fieldErrorProps, FieldSkeleton } from './utils';

export { LocaleTextField } from './locale';
export type { LocaleTextFieldProps } from './locale';
export { NumericField } from './numeric';
export type { NumericFieldProps } from './numeric';
export type { CurrencyFieldProps } from './currency';
export { CurrencyField } from './currency';
export { PercentField } from './percent';
export type { PercentFieldProps } from './percent';
export { ContextLine } from './context-line';
export type { ContextLineProps } from './context-line';

const sizes = ['small', 'medium'] satisfies TextFieldProps['size'][];

export const TextFieldSize = makeEnum(...sizes);
export type TextFieldSize = enumType<typeof TextFieldSize>;

export type TextFieldTextTransform = Extract<
    TypographyProps['textTransform'],
    'none' | 'uppercase' | 'lowercase'
>;

export type TextFieldProps<Multiline extends boolean | undefined = false> =
    TypedOmit<
        MuiTextFieldProps,
        'onChange' | 'multiline' | 'value' | 'error' | 'fullWidth'
    > & {
        value?: string;
        textTransform?: TextFieldTextTransform;
        multiline?: Multiline;
        clearable?: boolean;
        readOnly?: boolean;
        bgColor?: ColorProp;
        error?: React.ReactNode;
        mask?: TextMaskProps | TextMaskProps[];
        skeleton?: boolean;
        loading?: boolean;
        width?: number | string;
        maxLength?: number;
        showMaxLengthCounter?: boolean;
        localeOption?: LocaleOption;
        onChange?(
            value: string,
            event: React.ChangeEvent<
                Multiline extends true ? HTMLTextAreaElement : HTMLInputElement
            >
        ): void;
        onClear?(): void;
    };

export const TextField = forwardRef(
    <Multiline extends boolean | undefined>(
        props: TextFieldProps<Multiline>,
        ref: React.Ref<HTMLDivElement>
    ) => {
        const {
            size = 'small',
            readOnly = false,
            clearable = false,
            skeleton = false,
            loading = false,
            variant = 'outlined',
            value = '',
            error,
            mask,
            helperText,
            maxLength,
            showMaxLengthCounter,
            textTransform,
            slotProps,
            onChange,
            onClear,
            bgColor = 'transparent',
            width: _width,
            sx: _sx = {},
            localeOption,
            placeholder,
            ...muiProps
        } = props;
        const width = _width || '100%';

        const shrink = useInitialShrink(value, loading);
        const {
            theme: { typography }
        } = useRubiksTheme();

        const counter =
            showMaxLengthCounter && (maxLength ?? 0) > 0 ? (
                <MuiFormHelperText>
                    {(value ?? '').length}/{maxLength}
                </MuiFormHelperText>
            ) : null;

        const slotPropsInput = (() => {
            const endAdornment: React.ReactNode[] = [];

            if (loading) {
                endAdornment.push(
                    <MuiCircularProgress
                        key="loading"
                        size={20}
                        sx={{ color: 'text.secondary' }}
                    />
                );
            } else if (clearable && value !== '' && !readOnly) {
                endAdornment.push(
                    <IconButton
                        key="clear"
                        icon="IconClose"
                        onClick={() => {
                            onChange?.('', {
                                target: { value: '' }
                            } as React.ChangeEvent<
                                Multiline extends true
                                    ? HTMLTextAreaElement
                                    : HTMLInputElement
                            >);
                            onClear?.();
                        }}
                        size="small"
                    />
                );
            }

            const inputProps = slotProps?.input as InputProps | undefined;

            return {
                ...inputProps,
                ...(mask ? { inputComponent: TextMask } : {}),
                readOnly,
                endAdornment: (
                    <FlexBox gap={0.5} vAlign="center">
                        {inputProps?.endAdornment}
                        {endAdornment}
                    </FlexBox>
                )
            };
        })();

        const slotPropsInputLabel = slotProps?.inputLabel as
            | InputLabelProps
            | undefined;

        const slotPropsHtmlInput = slotProps?.htmlInput as
            | React.InputHTMLAttributes<HTMLInputElement>
            | undefined;

        const defaultHtmlInputProps = {
            'data-1p-ignore': true,
            maxLength
        };

        const inputProps = {
            ...slotPropsInput.inputProps,
            ...(Array.isArray(mask) ? { mask } : mask)
        };

        const readOnlyProps: SxProps<Theme> = readOnly
            ? {
                  '.MuiInputBase-root, .MuiInputBase-root > .MuiInputBase-input':
                      {
                          cursor: 'default',
                          userSelect: 'none'
                      },
                  '.MuiInputBase-root > .MuiInputBase-input:read-only::selection':
                      {
                          backgroundColor: 'transparent',
                          color: 'inherit'
                      }
              }
            : {};

        const transformedValue = transformTextFieldValue(value, textTransform);

        const sx = deepMerge<Exclude<SxProps<Theme>, null>>(
            {
                width,
                backgroundColor: bgColor,
                '.MuiInputBase-root': {
                    boxSizing: 'border-box',

                    '& .MuiInputBase-input': {
                        boxSizing: 'content-box',
                        ...(textTransform ? { textTransform } : {}),
                        ...typography.bodyEmphasis
                    }
                }
            },
            readOnlyProps,
            _sx as Exclude<SxProps<Theme>, null>
        );

        const textfield = (
            <MuiTextField
                ref={ref}
                size={size}
                variant={variant}
                value={transformedValue}
                {...fieldErrorProps(error, helperText)}
                slotProps={{
                    ...slotProps,
                    inputLabel: {
                        ...slotPropsInputLabel,
                        shrink,
                        sx: {
                            ...slotPropsInputLabel?.sx,
                            ...(shrink ? { transition: 'none' } : {}),
                            ...firstLetterUppercase
                        }
                    },
                    input: {
                        ...slotPropsInput,
                        ...(keys(inputProps).length ? { inputProps } : {})
                    },
                    ...(keys(slotPropsHtmlInput || {}).length
                        ? {
                              htmlInput: {
                                  ...defaultHtmlInputProps,
                                  ...slotPropsHtmlInput,
                                  readOnly:
                                      readOnly ?? slotPropsHtmlInput?.readOnly
                              }
                          }
                        : { htmlInput: defaultHtmlInputProps }),
                    formHelperText: {
                        sx: {
                            ...firstLetterUppercase
                        }
                    }
                }}
                data-role="text-field"
                onChange={(
                    e: React.ChangeEvent<
                        Multiline extends true
                            ? HTMLTextAreaElement
                            : HTMLInputElement
                    >
                ) =>
                    onChange?.(
                        transformTextFieldValue(e.target.value, textTransform),
                        e
                    )
                }
                sx={sx}
                placeholder={sentence(placeholder)}
                {...muiProps}
            />
        );

        const content = skeleton ? (
            <FieldSkeleton width={width}>{textfield}</FieldSkeleton>
        ) : (
            textfield
        );

        if (localeOption) {
            return (
                <Box>
                    <LocaleTextField
                        label={localeOption.label}
                        content={content}
                    />
                    {counter}
                </Box>
            );
        }

        return counter ? (
            <Box>
                {content}
                {counter}
            </Box>
        ) : (
            content
        );
    }
);

export type TextAreaProps = TypedOmit<TextFieldProps<true>, 'multiline'>;

export function TextArea(props: TextAreaProps) {
    return <TextField rows={4} {...props} multiline />;
}

const transformTextFieldValue = (
    value: string,
    textTransform: TextFieldTextTransform | undefined
) => {
    switch (textTransform) {
        case 'uppercase':
            return value.toUpperCase();
        case 'lowercase':
            return value.toLowerCase();
        default:
            return value;
    }
};

const getInputFontSize = (theme: Theme, variant: 'label' | 'valuer') =>
    theme.components?.input?.[variant]?.fontSize ??
    theme.palette.components?.input?.[variant]?.fontSize;

export const textfieldStyle = {
    MuiInputLabel: {
        styleOverrides: {
            root: ({ theme }) => {
                const valueFontSize =
                    getInputFontSize(theme, 'valuer') ??
                    theme.typography.subtitle1.fontSize;

                return {
                    fontSize: theme.typography.bodyEmphasis.fontSize,
                    marginBottom: 0,
                    [`&.${muiInputLabelClasses.shrink}`]: {
                        fontSize: valueFontSize
                    }
                };
            }
        }
    },
    MuiInputBase: {
        styleOverrides: {
            root: ({ theme }) => {
                const labelFontSize =
                    getInputFontSize(theme, 'label') ??
                    theme.typography.caption.fontSize;
                const inputFontSize = theme.typography.body1.fontSize;

                return {
                    fontSize: inputFontSize,
                    [`& .${muiOutlinedInputClasses.notchedOutline} > legend`]: {
                        fontSize: labelFontSize
                    }
                };
            }
        }
    }
} satisfies ThemeOptions['components'];
